import * as THREE from 'three';
import type { BattleState } from '@somnhold/sim';
import { Kit, place } from '../seasons/kit.ts';
import type { OmenCtx } from '../omens/common.ts';
import { GlowLayer } from './glow.ts';
import { heartMeshes } from './ribs.ts';

/**
 * El Dragón Ahogado (La Orilla 5, docs/SAGAS.md), over the fin's coast (engine/fin.ts draws the stilt-houses, the sea,
 * the lanes and the titan's fin). In both of the chapter's fights: the dragon's heart, if the group took it, carried
 * over its carrier's head or lying on the ground with a ring of heat pulsing round it. In the last fight
 * (`rules.sunken.boss`): Yorra's three boats out on the sea, a lantern on each; the harpoon's rope from the boat that
 * fired to the dragon while it holds; the band its breath will burn (a red warning on the ground while it draws
 * breath, a sheet of fire, or of steam with its heart gone, when it breathes); the fire in its chest; water running
 * off its bones; the fin crushing it (a great burst of spray and bone) or going past it; its rising and its fall.
 *
 * Draw calls: the boats (one merged mesh, plus one for their lanterns), the band (one), the rope (one), the heart (four
 * small meshes), a glow layer. No lights; nothing allocated per frame.
 */

type Rules = NonNullable<NonNullable<BattleState['rules']>['sunken']>;

const RED = new THREE.Color('#ff4f3a');
const EMBER = new THREE.Color('#ff8a3a');
const EMBER_CORE = new THREE.Color('#ffd2a0');
const STEAM = new THREE.Color('#dff0ff');
const LAMP = new THREE.Color('#ffd6a0');
/** How long the breath's sheet burns on the ground (seconds). */
const BREATH_S = 0.9;
/** The chest's fire, above the dragon's feet (world units, at its scale). */
const CHEST_Y = 1.9;
const NA = 20;

const bandMat = (): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
    polygonOffset: true,
    polygonOffsetFactor: -3,
    polygonOffsetUnits: -6,
    uniforms: {
      warn: { value: 0 },
      fire: { value: -1 },
      steam: { value: 0 },
      time: { value: 0 },
    },
    vertexShader: /* glsl */ `
      attribute vec2 band;
      varying vec2 vB;
      void main() {
        vB = band;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }`,
    fragmentShader: /* glsl */ `
      varying vec2 vB;
      uniform float warn;
      uniform float fire;
      uniform float steam;
      uniform float time;
      void main() {
        // vB.x: 0 at its mouth, 1 at the band's end; vB.y: -1..1 across.
        float across = abs(vB.y);
        float fade = smoothstep(0.0, 0.05, vB.x) * (1.0 - smoothstep(0.85, 1.0, vB.x));
        vec3 col = vec3(0.0);
        float a = 0.0;
        // Drawing breath: the band reddens from its mouth out, its edges pulsing, arrows running outward.
        if (warn > 0.0) {
          float fill = 1.0 - smoothstep(warn - 0.1, warn, vB.x);
          float pulse = 0.6 + 0.4 * sin(time * (9.0 + 9.0 * warn));
          float rim = smoothstep(0.7, 0.95, across);
          col = mix(vec3(0.35, 0.05, 0.02), vec3(1.0, 0.31, 0.23), rim * pulse);
          a = (0.18 * fill + rim * (0.5 + 0.35 * pulse)) * fade;
          float chev = step(0.88, fract(vB.x * 7.0 - time * 1.6 - across * 0.5)) * (1.0 - smoothstep(0.2, 0.35, across));
          col = mix(col, vec3(1.0, 0.45, 0.3), chev);
          a = max(a, chev * 0.7 * fade);
        }
        // The breath: a sheet of fire (or steam) racing out from its mouth and burning a moment.
        if (fire >= 0.0) {
          float front = fire * 3.0;
          float lit = 1.0 - smoothstep(front - 0.05, front, vB.x);
          float burn = 1.0 - smoothstep(0.55, 1.0, fire);
          float n = 0.5 + 0.5 * sin(vB.x * 40.0 - time * 22.0 + vB.y * 6.0) * sin(vB.x * 17.0 + time * 13.0);
          float core = 1.0 - smoothstep(0.0, 0.8, across);
          vec3 flame = mix(vec3(0.9, 0.18, 0.04), mix(vec3(1.0, 0.62, 0.22), vec3(1.0, 0.95, 0.8), core * n), core);
          vec3 vapour = mix(vec3(0.62, 0.74, 0.8), vec3(1.0), core * n);
          col = mix(col, mix(flame, vapour, steam), lit);
          a = max(a, lit * burn * (0.55 + 0.4 * n) * (1.0 - smoothstep(0.85, 1.0, across)) * fade);
        }
        gl_FragColor = vec4(col, a);
      }`,
  });

export class SunkenLayer {
  readonly group = new THREE.Group();
  private mats: THREE.Material[] = [];
  private geos: THREE.BufferGeometry[] = [];
  private glow = new GlowLayer(220);
  private heart: THREE.Group;
  private band: THREE.Mesh;
  private bandM: THREE.ShaderMaterial;
  private bandPos: THREE.BufferAttribute;
  private rope: THREE.Mesh;
  private boats: THREE.Vector3[] = [];
  private lamps: THREE.Vector3[] = [];
  private now = 0;
  /** The breath's last start (frame clock), the boat that fired last (index). */
  private breathAt = -Infinity;
  private boat = -1;
  /** Where the dragon was drawn last (world), its id. */
  private at = new THREE.Vector3();
  private seen = false;
  private v = new THREE.Vector3();
  private v2 = new THREE.Vector3();
  /** Where the heart lies on the ground (sim) and its world point. */
  private lay0 = { x: NaN, y: NaN, p: new THREE.Vector3() };
  private q = new THREE.Quaternion();

  constructor(
    private ctx: OmenCtx,
    private r: Rules,
    /** The sea's surface (world y), from the fin's layer. */
    private seaY: number,
  ) {
    this.group.name = 'sunken';
    const box = new THREE.BoxGeometry(1, 1, 1);
    const cyl = new THREE.CylinderGeometry(0.5, 0.5, 1, 8);
    const ball = new THREE.SphereGeometry(0.5, 8, 6);
    const hull = new THREE.LatheGeometry(
      [[0.0, -0.5], [0.3, -0.45], [0.47, -0.24], [0.5, 0], [0.45, 0], [0.42, -0.22], [0.27, -0.38], [0.0, -0.42]].map(([x, y]) => new THREE.Vector2(x as number, y as number)),
      14,
    );
    const kit = new Kit();
    const lit = new Kit();
    const b = r.boss;
    // ---- Yorra's boats: out on the sea past the rim, bows to the shore, a harpooner's crossbow on the bow.
    for (const at of b?.boats ?? []) {
      const len = Math.max(1, Math.hypot(at.x, at.y));
      const out = 3600;
      const w = ctx.at(at.x + (at.x / len) * out, at.y + (at.y / len) * out, 0);
      const yaw = Math.atan2(-w.x, -w.z);
      const y = seaY + 0.12;
      const s = new THREE.Vector3(1.2, 0.5, 2.6);
      const m = place(w.x, y + 0.08, w.z, yaw);
      kit.add(hull, '#3e6a64', m, new THREE.Matrix4().makeScale(s.x, s.y, s.z).premultiply(new THREE.Matrix4().makeRotationY(Math.PI / 2)));
      kit.add(new THREE.TorusGeometry(0.5, 0.04, 4, 16).rotateX(Math.PI / 2), '#8a3a30', m, new THREE.Matrix4().makeScale(1.22, 1, 2.6).premultiply(new THREE.Matrix4().makeTranslation(0, -0.06, 0)));
      kit.add(new THREE.TorusGeometry(0.5, 0.04, 4, 16).rotateX(Math.PI / 2), '#6c665a', m, new THREE.Matrix4().makeScale(1.22, 1, 2.6).premultiply(new THREE.Matrix4().makeTranslation(0, 0.0, 0)));
      for (const z of [-0.45, 0.35]) kit.add(box, '#8a7a5a', m, new THREE.Matrix4().compose(new THREE.Vector3(0, 0.0, z), new THREE.Quaternion(), new THREE.Vector3(1.0, 0.05, 0.12)));
      // The mast with a furled sail, the lantern on a pole at the stern, the crossbow and its coil at the bow.
      kit.add(cyl, '#3a3026', m, new THREE.Matrix4().compose(new THREE.Vector3(0, 0.95, -0.1), new THREE.Quaternion(), new THREE.Vector3(0.07, 1.9, 0.07)));
      kit.add(cyl, '#6c665a', m, new THREE.Matrix4().compose(new THREE.Vector3(0, 1.45, -0.1), new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1, 0, 0), Math.PI / 2), new THREE.Vector3(0.12, 1.0, 0.12)));
      kit.add(cyl, '#3a3026', m, new THREE.Matrix4().compose(new THREE.Vector3(0, 0.55, -1.05), new THREE.Quaternion(), new THREE.Vector3(0.04, 1.1, 0.04)));
      kit.add(box, '#2a2622', m, new THREE.Matrix4().compose(new THREE.Vector3(0, 0.22, 1.0), new THREE.Quaternion(), new THREE.Vector3(0.7, 0.08, 0.12)));
      kit.add(box, '#2a2622', m, new THREE.Matrix4().compose(new THREE.Vector3(0, 0.22, 0.9), new THREE.Quaternion(), new THREE.Vector3(0.1, 0.1, 0.6)));
      kit.add(new THREE.TorusGeometry(0.16, 0.05, 4, 10).rotateX(Math.PI / 2), '#9a8566', m, new THREE.Matrix4().makeTranslation(0.25, 0.08, 0.6));
      // Two fishers, hooded, one at the crossbow.
      for (const [x, z] of [[0.1, 0.65], [-0.15, -0.6]] as const) {
        kit.add(cyl, '#2c3438', m, new THREE.Matrix4().compose(new THREE.Vector3(x, 0.32, z), new THREE.Quaternion(), new THREE.Vector3(0.28, 0.5, 0.28)));
        kit.add(ball, '#1e2428', m, new THREE.Matrix4().compose(new THREE.Vector3(x, 0.65, z), new THREE.Quaternion(), new THREE.Vector3(0.24, 0.26, 0.24)));
      }
      lit.add(ball, '#ffd6a0', m, new THREE.Matrix4().compose(new THREE.Vector3(0, 1.1, -1.05), new THREE.Quaternion(), new THREE.Vector3(0.16, 0.2, 0.16)));
      this.boats.push(new THREE.Vector3(w.x, y + 0.35, w.z).add(new THREE.Vector3(Math.sin(yaw), 0, Math.cos(yaw)).multiplyScalar(1.0 * 1)));
      this.lamps.push(new THREE.Vector3(0, 1.1, -1.05).applyMatrix4(m));
    }
    const mat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.82, metalness: 0.02, flatShading: true });
    const litMat = new THREE.MeshBasicMaterial({ vertexColors: true, toneMapped: false });
    this.mats.push(mat, litMat);
    for (const [k, mm, name] of [
      [kit, mat, 'sunken-boats'],
      [lit, litMat, 'sunken-lamps'],
    ] as const) {
      const mesh = k.build(mm, name);
      if (!mesh) continue;
      mesh.castShadow = mm === mat && !ctx.low;
      this.geos.push(mesh.geometry);
      this.group.add(mesh);
    }
    for (const g of [box, cyl, ball, hull]) g.dispose();

    // ---- The breath's band: a strip laid on the ground from its mouth out (re-laid when it aims).
    const pos = new Float32Array((NA + 1) * 3 * 3);
    const band: number[] = [];
    const idx: number[] = [];
    for (let i = 0; i <= NA; i++) for (let j = 0; j < 3; j++) band.push(i / NA, j - 1);
    for (let i = 0; i < NA; i++)
      for (let j = 0; j < 2; j++) {
        const a = i * 3 + j;
        const c = a + 3;
        idx.push(a, c, a + 1, a + 1, c, c + 1);
      }
    const bandG = new THREE.BufferGeometry();
    this.bandPos = new THREE.BufferAttribute(pos, 3);
    bandG.setAttribute('position', this.bandPos);
    bandG.setAttribute('band', new THREE.Float32BufferAttribute(band, 2));
    bandG.setIndex(idx);
    this.bandM = bandMat();
    this.bandM.uniforms.steam!.value = b?.steam ? 1 : 0;
    this.band = new THREE.Mesh(bandG, this.bandM);
    this.band.name = 'sunken-breath';
    this.band.renderOrder = 4;
    this.band.frustumCulled = false;
    this.band.visible = false;
    this.geos.push(bandG);
    this.mats.push(this.bandM);
    this.group.add(this.band);

    // ---- The harpoon's rope.
    const ropeG = new THREE.CylinderGeometry(0.05, 0.05, 1, 5).translate(0, 0.5, 0);
    const ropeM = new THREE.MeshBasicMaterial({ color: '#d8c49a' });
    this.geos.push(ropeG);
    this.mats.push(ropeM);
    this.rope = new THREE.Mesh(ropeG, ropeM);
    this.rope.visible = false;
    this.rope.frustumCulled = false;
    this.rope.name = 'sunken-rope';
    this.group.add(this.rope);

    // ---- The heart.
    const h = heartMeshes();
    this.heart = h.group;
    this.heart.visible = false;
    this.mats.push(...h.mats);
    this.geos.push(...h.geos);
    this.group.add(this.heart);

    this.group.add(this.glow.mesh);
  }

  /** Lays the breath's band from (x, y) towards (tx, ty), `range` long and `half` wide each side (sim units). */
  private lay(x: number, y: number, tx: number, ty: number): void {
    const b = this.r.boss;
    if (!b) return;
    const len = Math.max(1, Math.hypot(tx - x, ty - y));
    const ux = (tx - x) / len;
    const uy = (ty - y) / len;
    const arr = this.bandPos.array as Float32Array;
    for (let i = 0; i <= NA; i++) {
      const a = (b.breath.range * i) / NA;
      for (let j = 0; j < 3; j++) {
        const off = (j - 1) * b.breath.half;
        const w = this.ctx.at(x + ux * a - uy * off, y + uy * a + ux * off, 0);
        const k = (i * 3 + j) * 3;
        arr[k] = w.x;
        arr[k + 1] = Math.max(this.ctx.ground(w.x, w.z), this.seaY) + 0.14;
        arr[k + 2] = w.z;
      }
    }
    this.bandPos.needsUpdate = true;
    this.band.geometry.computeBoundingSphere();
  }

  /** Every fresh tick: its moments (rise, breath, harpoon, the fin on it, its fall), water off its bones, the heart's ring. */
  tick(s: BattleState): void {
    const low = this.ctx.low;
    const b = this.r.boss;
    const steam = !!b?.steam;
    for (const e of s._events ?? []) {
      if (e.t !== 'sunken') continue;
      const p = this.ctx.at(e.x, e.y, 0);
      p.y = Math.max(this.ctx.ground(p.x, p.z), this.seaY) + 0.2;
      if (e.kind === 'rise') {
        // It climbs out of the sea: a wall of spray, a deep shake.
        this.ctx.shake(0.6);
        this.ctx.vfx.ring(p, 0.4, 4.5, '#cfe8ff', 18, 0.8);
        this.ctx.vfx.burst(p, { n: low ? 14 : 36, color: '#e6f2ff', speed: 0.1, up: 0.16, size: 0.16, life: 28, gravity: 0.012, spread: 1.6 });
        this.ctx.flare(p.clone().setY(p.y + 2), steam ? '#dff0ff' : '#ff6a2a', 3.6, 24);
      } else if (e.kind === 'draw') {
        this.lay(e.x, e.y, e.tx, e.ty);
        this.ctx.flare(p.clone().setY(p.y + CHEST_Y), steam ? '#dff0ff' : '#ff6a2a', 3.4, 18);
      } else if (e.kind === 'breath') {
        this.lay(e.x, e.y, e.tx, e.ty);
        this.breathAt = this.now;
        this.ctx.shake(0.35);
        const end = this.ctx.at(e.tx, e.ty, 0);
        const n = low ? 5 : 11;
        for (let k = 0; k < n; k++) {
          this.v.lerpVectors(p, end, k / (n - 1));
          this.v.y = Math.max(this.ctx.ground(this.v.x, this.v.z), this.seaY) + 0.3;
          if (steam) this.ctx.vfx.burst(this.v, { n: low ? 2 : 4, color: '#eef6fa', speed: 0.03, up: 0.05, size: 0.5, life: 30, smoke: true, spread: 0.6 });
          else this.ctx.vfx.burst(this.v, { n: low ? 3 : 7, color: k % 2 ? '#ffb070' : '#ff6a2a', speed: 0.05, up: 0.09, size: 0.14, life: 18, gravity: -0.004, spread: 0.7 });
        }
        this.ctx.flare(p.clone().setY(p.y + 2.2), steam ? '#dff0ff' : '#ffb070', 3, 14);
      } else if (e.kind === 'harpoon') {
        // From the nearest of the boats: a crack, a spray at the bow.
        let best = 0;
        let bd = Infinity;
        this.boats.forEach((w, i) => {
          const d = (w.x - p.x) ** 2 + (w.z - p.z) ** 2;
          if (d < bd) {
            bd = d;
            best = i;
          }
        });
        this.boat = this.boats.length ? best : -1;
        const w = this.boats[best];
        if (w) {
          this.ctx.flare(w.clone().setY(w.y + 0.6), '#ffd6a0', 1.6, 10);
          this.ctx.vfx.burst(w, { n: low ? 4 : 10, color: '#dff0ff', speed: 0.05, up: 0.07, size: 0.08, life: 14, gravity: 0.012, spread: 0.4 });
        }
        const d = this.ctx.at(e.tx, e.ty, 0);
        d.y = this.ctx.ground(d.x, d.z) + 1.4;
        this.ctx.vfx.burst(d, { n: low ? 5 : 12, color: '#b8b0a4', speed: 0.06, up: 0.06, size: 0.07, life: 14, gravity: 0.015, spread: 0.3 });
      } else if (e.kind === 'crush') {
        // The fin comes down on it: a mountain of spray, its bones bursting, the titan shuddering.
        this.ctx.shake(1.0);
        this.ctx.flare(p.clone().setY(p.y + 2.5), '#e6f2ff', 3.6, 20);
        this.ctx.vfx.ring(p, 0.5, 6, '#e6f2ff', 22, 0.9);
        this.ctx.vfx.ring(p, 0.3, 3.5, '#ffffff', 14, 0.9);
        this.ctx.vfx.burst(p, { n: low ? 18 : 48, color: '#f2f8ff', speed: 0.14, up: 0.2, size: 0.18, life: 32, gravity: 0.012, spread: 1.8 });
        this.ctx.vfx.burst(p.clone().setY(p.y + 1.2), { n: low ? 10 : 26, color: '#8f877c', speed: 0.12, up: 0.14, size: 0.12, life: 30, gravity: 0.016, spread: 0.9 });
        this.ctx.vfx.burst(p.clone().setY(p.y + 1.2), { n: low ? 4 : 10, color: steam ? '#dff0ff' : '#ff8a3a', speed: 0.08, up: 0.12, size: 0.1, life: 22, gravity: -0.002, spread: 0.6 });
      } else if (e.kind === 'shrug') {
        // It got out of the way: only spray at its feet.
        this.ctx.vfx.burst(p, { n: low ? 6 : 14, color: '#dff0ff', speed: 0.07, up: 0.1, size: 0.1, life: 18, gravity: 0.014, spread: 0.9 });
      } else if (e.kind === 'felled') {
        // The fire goes out in a burst of steam; its bones come apart.
        this.ctx.shake(0.8);
        this.ctx.flare(p.clone().setY(p.y + 2), steam ? '#dff0ff' : '#ff6a2a', 4, 28);
        this.ctx.vfx.burst(p.clone().setY(p.y + 1.6), { n: low ? 8 : 18, color: '#e8f2f6', speed: 0.04, up: 0.06, size: 0.9, life: 50, smoke: true, spread: 1.2 });
        this.ctx.vfx.burst(p.clone().setY(p.y + 1.2), { n: low ? 14 : 36, color: '#8f877c', speed: 0.12, up: 0.15, size: 0.13, life: 36, gravity: 0.016, spread: 1.4 });
        this.ctx.vfx.ring(p, 0.4, 5, '#cfe8ff', 22, 0.8);
      } else if (e.kind === 'drop') {
        this.ctx.vfx.ring(p, 0.2, 1.4, '#ff4f3a', 12, 0.8);
      } else if (e.kind === 'lift') {
        this.ctx.flare(p.clone().setY(p.y + 1.6), '#ff6a3a', 2.2, 12);
      }
    }
    // Water running off its bones, and the fire breathing sparks (or steam) out of its ribs.
    const d = b && s.sunken?.id ? s.enemies.find((x) => x.id === s.sunken?.id && x.hp > 0) : undefined;
    if (d) {
      const p = this.ctx.pos(d.id);
      if (p && s.tick % (low ? 4 : 2) === 0) {
        const a = s.tick * 2.3;
        this.v.set(p.x + Math.cos(a) * 0.9, p.y + 1.4 + 0.5 * Math.sin(a * 1.7), p.z + Math.sin(a) * 0.9);
        this.ctx.vfx.burst(this.v, { n: 1, color: '#cfe8ff', speed: 0.005, up: -0.01, size: 0.06, life: 16, gravity: 0.016, spread: 0.1 });
        if (s.tick % (low ? 8 : 4) === 0) {
          this.v.set(p.x, p.y + CHEST_Y + 0.4, p.z);
          if (steam) this.ctx.vfx.burst(this.v, { n: 1, color: '#eef6fa', speed: 0.01, up: 0.04, size: 0.45, life: 30, smoke: true, spread: 0.3 });
          else this.ctx.vfx.burst(this.v, { n: 1, color: '#ffb070', speed: 0.01, up: 0.04, size: 0.05, life: 20, gravity: -0.003, spread: 0.3 });
        }
      }
    }
    // The heart dropped: a warm ring pulsing round it.
    const hs = s.sunken?.heart;
    if (hs?.at === 'ground' && s.tick % 10 === 0) this.ctx.vfx.ring(this.ctx.at(hs.x, hs.y, 0.05), 0.3, 1.3, '#ff4f3a', 10, 0.7);
  }

  /** Every redraw: the band, the rope, the heart, the chest's fire, the boats' lanterns. */
  draw(s: BattleState): void {
    const b = this.r.boss;
    const st = s.sunken;
    const d = b && st?.id ? s.enemies.find((x) => x.id === st.id && x.hp > 0) : undefined;
    const dp = d ? this.ctx.pos(d.id) : undefined;
    if (dp) {
      this.at.copy(dp);
      this.seen = true;
    } else if (!d) this.seen = false;
    // The band: red while it draws breath, fire (or steam) for a moment after.
    const since = this.now - this.breathAt;
    const drawing = !!st?.draw && !!b;
    const burning = since < BREATH_S;
    this.band.visible = drawing || burning;
    this.bandM.uniforms.warn!.value = drawing && b ? Math.min(1, Math.max(0.05, 1 - (st.draw - s.tick) / Math.max(1, b.breath.draw))) : 0;
    this.bandM.uniforms.fire!.value = burning ? since / BREATH_S : -1;
    this.bandM.uniforms.time!.value = this.now;
    // The rope, from the boat that fired to the dragon, while the harpoon holds it.
    const held = !!b && !!st && st.held > s.tick && this.boat >= 0 && this.seen;
    this.rope.visible = held;
    if (held) {
      const from = this.boats[this.boat] as THREE.Vector3;
      this.v.copy(this.at).setY(this.at.y + 1.3).sub(from);
      const len = this.v.length();
      this.q.setFromUnitVectors(UPV, this.v.normalize());
      this.rope.position.copy(from);
      this.rope.quaternion.copy(this.q);
      this.rope.scale.set(1, len, 1);
    }
    // The heart: over its carrier's head, or on the ground.
    const hs = st?.heart;
    this.heart.visible = !!hs;
    if (hs) {
      if (hs.at === 'carried') {
        const c = s.heroes.find((h) => h.pid === hs.pid);
        const p = c ? (this.ctx.pos(c.id) ?? this.ctx.at(c.x, c.y, 0)) : this.ctx.at(hs.x, hs.y, 0);
        this.heart.position.set(p.x, this.ctx.ground(p.x, p.z) + 1.95, p.z);
        this.heart.scale.setScalar(0.85);
      } else {
        // Lying where it fell (looked up only when it moves).
        if (this.lay0.x !== hs.x || this.lay0.y !== hs.y) {
          this.lay0.x = hs.x;
          this.lay0.y = hs.y;
          this.v2.copy(this.ctx.at(hs.x, hs.y, 0));
          this.lay0.p.set(this.v2.x, this.v2.y + 0.3, this.v2.z);
        }
        this.heart.position.copy(this.lay0.p);
        this.heart.scale.setScalar(0.75);
      }
      this.heart.rotation.y = this.now * 0.4;
    }
    this.glow.begin();
    const beat = 0.5 + 0.5 * Math.sin(this.now * 2.4);
    if (hs) {
      const p = this.heart.position;
      const sc = this.heart.scale.x;
      this.glow.add(p.x, p.y, p.z, RED, 1.8 * sc, 0.14 + 0.08 * beat);
      this.glow.add(p.x, p.y, p.z, EMBER, 0.9 * sc, 0.3 + 0.2 * beat);
      this.glow.add(p.x, p.y, p.z, EMBER_CORE, 0.4 * sc, 0.5 + 0.3 * beat);
    }
    // The fire in its chest lights the ground round it; brighter while it draws breath.
    if (this.seen && d) {
      const k = drawing ? 1 : 0;
      const f = 0.5 + 0.5 * Math.sin(this.now * 7);
      const c = b?.steam ? STEAM : EMBER;
      this.glow.add(this.at.x, this.at.y + CHEST_Y, this.at.z, c, 2.4 + 1.6 * k, 0.16 + 0.08 * f + 0.25 * k);
      this.glow.add(this.at.x, this.ctx.ground(this.at.x, this.at.z) + 0.06, this.at.z, b?.steam ? STEAM : RED, 3.4 + k, 0.08 + 0.12 * k);
    }
    for (const l of this.lamps) this.glow.add(l.x, l.y, l.z, LAMP, 0.9, 0.55 + 0.15 * Math.sin(this.now * 3 + l.x));
    this.glow.end();
  }

  frame(now: number): void {
    this.now = now;
  }

  dispose(): void {
    for (const g of this.geos) g.dispose();
    for (const m of this.mats) m.dispose();
    this.glow.mesh.geometry.dispose();
    this.glow.material.dispose();
  }
}

const UPV = new THREE.Vector3(0, 1, 0);
